import { jsPDF } from "jspdf";
import { money, planLabel, roleLabel, statusLabel, ACCOUNT_STATUSES } from "./constants.js";
import { reportLines } from "./reports.js";
import { getOrganization, listAccounts, listInvoices, listPayments, subscriptionFor } from "./store.js";

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function downloadBlob(filename, mime, body) {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function exportAccountsCsv() {
  const header = [
    "id",
    "name",
    "email",
    "phone",
    "role",
    "status",
    "organization",
    "plan",
    "subscription_status",
    "city",
    "county",
    "state",
    "created_at",
    "last_login",
  ];
  const lines = [
    header.join(","),
    ...listAccounts().map((row) => {
      const org = row.organization_id ? getOrganization(row.organization_id) : null;
      const sub = subscriptionFor(row.id) || (row.organization_id ? subscriptionFor(row.organization_id) : null);
      return [
        row.id,
        row.name,
        row.email,
        row.phone,
        row.role,
        row.status,
        org?.name || "",
        sub?.plan_id || "",
        sub?.status || "",
        row.city,
        row.county,
        row.state,
        row.created_at,
        row.last_login,
      ]
        .map(csvEscape)
        .join(",");
    }),
  ];
  downloadBlob("rechecklist-accounts.csv", "text/csv", lines.join("\n"));
}

export function exportTaxCsv() {
  const header = ["period_or_jurisdiction", "kind", "amount", "status", "number"];
  const invoiceLines = listInvoices().map((row) =>
    [row.issued_at?.slice(0, 7), row.tax_jurisdiction || "Untagged", row.amount, row.status, row.number].map(csvEscape).join(",")
  );
  const paymentLines = listPayments().map((row) =>
    [String(row.at).slice(0, 7), row.result, row.amount, row.result, row.id].map(csvEscape).join(",")
  );
  downloadBlob("rechecklist-tax.csv", "text/csv", [header.join(","), ...invoiceLines, ...paymentLines].join("\n"));
}

export async function exportAccountsPdf() {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const margin = 18;
  let y = 20;
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  doc.setTextColor(14, 61, 44);
  doc.text("REChecklist Account Management", margin, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(92, 74, 68);
  doc.text(`Prepared ${new Date().toLocaleString()} · Program desk record. Not a tax filing.`, margin, y);
  y += 10;
  reportLines().forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(value), margin + 70, y);
    y += 6;
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
  });
  y += 4;
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.setTextColor(14, 61, 44);
  doc.text("Directory", margin, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  listAccounts().forEach((row) => {
    const sub = subscriptionFor(row.id) || (row.organization_id ? subscriptionFor(row.organization_id) : null);
    const line = `${row.name} · ${roleLabel(row.role)} · ${statusLabel(ACCOUNT_STATUSES, row.status)} · ${
      sub ? planLabel(sub.plan_id) : "No plan"
    }`;
    const wrapped = doc.splitTextToSize(line, 180);
    wrapped.forEach((part) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(part, margin, y);
      y += 5;
    });
  });
  doc.save("rechecklist-accounts.pdf");
}

export function moneyLines(rows, key = "amount") {
  return rows.map((row) => ({ ...row, pretty: money(row[key]) }));
}
